import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  useTheme,
  useMediaQuery,
  Skeleton,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import InfoIcon from "@mui/icons-material/Info";
import SecurityIcon from "@mui/icons-material/Security";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import UpdateIcon from "@mui/icons-material/Update";
import DescriptionIcon from "@mui/icons-material/Description";
import SettingsIcon from "@mui/icons-material/Settings";
import PaymentIcon from "@mui/icons-material/Payment";
import HistoryIcon from "@mui/icons-material/History";
import GavelIcon from "@mui/icons-material/Gavel";
import { colors, fonts, size } from "../../ColorFont/color";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";

const PrivacyPolicy = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 500); // Simulate loading for 500ms
    return () => clearTimeout(timer);
  }, []);

  const sections = [
    {
      icon: <DescriptionIcon sx={{ fontSize: isMobile ? 24 : 30 }} />,
      title: t("ppPoint1"),
      content: t("ppDesc1"),
    },
    {
      icon: <SettingsIcon sx={{ fontSize: isMobile ? 24 : 30 }} />,
      title: t("ppPoint2"),
      content: t("ppDesc2"),
    },
    {
      icon: <PaymentIcon sx={{ fontSize: isMobile ? 24 : 30 }} />,
      title: t("ppPoint3"),
      content: t("ppDesc3"),
    },
    {
      icon: <HistoryIcon sx={{ fontSize: isMobile ? 24 : 30 }} />,
      title: t("ppPoint4"),
      content: t("ppDesc4"),
    },
    {
      icon: <GavelIcon sx={{ fontSize: isMobile ? 24 : 30 }} />,
      title: t("ppPoint5"),
      content: t("ppDesc5"),
    },
    {
      icon: <VerifiedUserIcon sx={{ fontSize: isMobile ? 24 : 30 }} />,
      title: t("ppPoint6"),
      content: t("ppDesc6"),
    },
  ];

  return (
    <Box sx={{ margin: "0 auto", maxWidth: "1200px", px: 0 }}>


      <Box
        sx={{
          maxWidth: "1200px",
          mx: "auto",
          p: 0,
          borderRadius: isMobile ? 2 : 3,
        }}
      >
        {/* Intro Section */}
        {loading ? (
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.3 }}
          >
            <Box sx={{ mb: isMobile ? 2 : 3 }}>
              <Skeleton variant="text" width="80%" sx={{ mx: "auto", mb: 1 }} />
              <Skeleton variant="text" width="90%" sx={{ mx: "auto" }} />
              <Skeleton variant="text" width="70%" sx={{ mx: "auto" }} />
            </Box>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.3 }}
          >
            <Typography
              sx={{
                fontFamily: fonts.poppins,
                fontSize: isMobile ? '13px' : '15px',
                textAlign: 'left',
                color: colors.text,
                mb: isMobile ? 2 : 3,
                px: isMobile ? 1 : 0,
              }}
            >
              {t("ppDesc")}
            </Typography>
          </motion.div>
        )}

        {/* Privacy Sections */}
        {loading ? (
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.5 }}
          >
            {Array.from({ length: 6 }).map((_, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ 
                  duration: 0.5, 
                  ease: "easeOut", 
                  delay: 0.7 + (index * 0.1) 
                }}
              >
                <Box
                  sx={{
                    p: isMobile ? 1.5 : 2,
                    borderRadius: isMobile ? 2 : 3,
                    mb: isMobile ? 2 : 3,
                    backgroundColor: "#fff",
                    borderLeft: isMobile
                      ? `4px solid #e0e0e0`
                      : `6px solid #e0e0e0`,
                    boxShadow: "0px 1px 4px rgba(0,0,0,0.05)",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "flex-start", mb: isMobile ? 0.5 : 1 }}>
                    <Skeleton variant="circular" width={isMobile ? 24 : 30} height={isMobile ? 24 : 30} sx={{ mr: isMobile ? 1 : 2 }} />
                    <Skeleton variant="text" width="70%" height={isMobile ? 20 : 24} />
                  </Box>
                  <Skeleton variant="text" width="95%" height={isMobile ? 16 : 20} sx={{ ml: isMobile ? "28px" : "38px" }} />
                  <Skeleton variant="text" width="85%" height={isMobile ? 16 : 20} sx={{ ml: isMobile ? "28px" : "38px" }} />
                </Box>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.5 }}
          >
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ 
                  duration: 0.5, 
                  ease: "easeOut", 
                  delay: 0.7 + (index * 0.1) 
                }}
              >
                <Box
                  sx={{
                    p: isMobile ? 1.5 : 2,
                    borderRadius: isMobile ? 2 : 3,
                    mb: isMobile ? 2 : 3,
                    backgroundColor: "#fff",
                    borderLeft: isMobile
                      ? `4px solid ${colors.primary_dark}`
                      : `6px solid ${colors.primary_dark}`,
                    boxShadow: "0px 1px 4px rgba(0,0,0,0.1)",
                    transition: isMobile ? "none" : "transform 0.2s ease-in-out",
                    "&:hover": {
                      transform: isMobile ? "none" : "scale(1.02)",
                      backgroundColor: colors.background_light,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      mb: isMobile ? 0.5 : 1,
                    }}
                  >
                    <Box
                      sx={{
                        mr: isMobile ? 1 : 2,
                        mt: isMobile ? 0.5 : 0,
                        color: colors.primary_dark,
                      }}
                    >
                      {section.icon}
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: fonts.poppins,
                        fontWeight: 600,
                        fontSize: isMobile ? size.smallLabel : "16px",
                        color: colors.dark,
                        lineHeight: 1.3,
                      }}
                    >
                      {index + 1}. {section.title}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: fonts.poppins,
                      fontSize: isMobile ? "12px" : "14px",
                      color: colors.text,
                      pl: isMobile ? "28px" : "38px",
                      lineHeight: 1.5,
                    }}
                  >
                    {section.content}
                  </Typography>
                </Box>
              </motion.div>
            ))}
          </motion.div>
        )}

        <Box>
        {loading ? (
          <Skeleton variant="text" width="80%" sx={{ mx: "auto", mb: 1 }} />
        ) : (
          <Typography 
            sx={{
              fontFamily: fonts.poppins, 
              textAlign: "center", 
              marginTop: isMobile ? 3 : 5, 
              fontSize: isMobile ? "13px" : "15px", 
              color: colors.text,
              backgroundColor: "colors.primary_rgba_5",
              p: 3,
              borderRadius: 3,
              border: `1px dashed ${colors.primary_dark}80`,
            }}
          >
            {t("footerPP")}
            <a
              href="mailto:krushisetuofficial@gmail.com"
              style={{
                marginLeft: "8px",
                color: colors.primary_dark,
                fontWeight: 600,
                textDecoration: "underline",
              }}
            >
              krushisetuofficial@gmail.com
            </a>
          </Typography>
        )}
        </Box>

        {/* Footer Section */}
        <Divider sx={{ marginY: isMobile ? 2 : 3 }} />
        {loading ? (
          <Skeleton variant="text" width="70%" sx={{ mx: "auto" }} />
        ) : (
          <Typography
            sx={{
              textAlign: "center",
              fontFamily: fonts.poppins,
              fontSize: isMobile ? "12px" : "14px",
              color: colors.text,
              px: isMobile ? 1 : 0,
            }}
          >
            {t("footer")}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default PrivacyPolicy;
